import { callEdgeFunction } from './supabaseClient';

const CHECK_STATUS_LABELS = {
  open: 'Açık',
  closed: 'Kapalı',
  cancel: 'İptal',
  draft: 'Taslak',
  new: 'Yeni',
  merged: 'Birleştirilmiş',
};

export function checkStatusLabel(status) {
  if (!status) return '—';
  return CHECK_STATUS_LABELS[status] || status;
}

export function parseSimpraCheckResponse(res) {
  if (!res || typeof res !== 'object') return null;
  const check = res.check ?? res;
  if (!check?.id) return null;
  return check;
}

/** Property-scoped Simpra discount id from an applied check_discounts row. */
export function pickPropertyDiscountIdFromCheck(check) {
  const rows = check?.check_discounts;
  if (!Array.isArray(rows) || !rows.length) return null;
  for (const row of rows) {
    const id = row?.discount_id ?? row?.discount?.id;
    if (id) return String(id);
  }
  return null;
}

export function buildDiscountGroupLookupIndex(groups) {
  const globalIds = new Set();
  const byGlobalAndProperty = new Map();
  const byPropertyDiscountId = new Map();
  const membersByGlobal = new Map();

  (groups || []).forEach((group) => {
    const globalId = group?.global_discount_id;
    if (!globalId) return;
    const globalKey = String(globalId).toLowerCase();
    globalIds.add(globalKey);

    const members = [];
    (group.simpra_discount_group_members || []).forEach((member) => {
      if (!member?.property_id || !member?.property_discount_id) return;
      const propertyId = String(member.property_id);
      const propertyDiscountId = String(member.property_discount_id);
      const mapKey = `${globalKey}::${propertyId.toLowerCase()}`;
      byGlobalAndProperty.set(mapKey, propertyDiscountId);
      byPropertyDiscountId.set(propertyDiscountId.toLowerCase(), {
        globalDiscountId: String(globalId),
        propertyId,
        propertyDiscountId,
        shopLabel: member.shop_label || null,
      });
      members.push({
        propertyId,
        propertyDiscountId,
        shopLabel: member.shop_label || null,
      });
    });
    membersByGlobal.set(globalKey, members);
  });

  return {
    globalIds,
    resolveGlobal(globalDiscountId, propertyId) {
      if (!globalDiscountId || !propertyId) return null;
      const key = `${String(globalDiscountId).toLowerCase()}::${String(propertyId).toLowerCase()}`;
      return byGlobalAndProperty.get(key) || null;
    },
    isGlobalId(id) {
      return !!id && globalIds.has(String(id).toLowerCase());
    },
    isPropertyDiscountId(id) {
      return !!id && byPropertyDiscountId.has(String(id).toLowerCase());
    },
    findByPropertyDiscountId(propertyDiscountId) {
      if (!propertyDiscountId) return null;
      return byPropertyDiscountId.get(String(propertyDiscountId).toLowerCase()) || null;
    },
    getMembersForGlobal(globalDiscountId) {
      if (!globalDiscountId) return [];
      return membersByGlobal.get(String(globalDiscountId).toLowerCase()) || [];
    },
    /** Global key or any branch property id → property id for this check's property. */
    resolveForProperty(discountId, propertyId) {
      if (!discountId || !propertyId) return null;
      const id = String(discountId);
      const pid = String(propertyId).toLowerCase();

      if (this.isGlobalId(id)) {
        return this.resolveGlobal(id, propertyId);
      }

      const member = this.findByPropertyDiscountId(id);
      if (member) {
        if (member.propertyId.toLowerCase() === pid) return member.propertyDiscountId;
        return this.resolveGlobal(member.globalDiscountId, propertyId);
      }

      return id;
    },
  };
}

/** Property discount id from discount group for this branch (apply target). */
export function resolveGroupPropertyDiscountId(assignedGlobalId, propertyId, groupIndex) {
  if (!assignedGlobalId || !propertyId || !groupIndex) return null;
  return groupIndex.resolveGlobal(assignedGlobalId, propertyId);
}

/**
 * Canonical applied property discount id for display/DB — from group mapping, not Simpra check.
 * Check may show a different legacy id; group is source of truth for apply.
 */
export function resolveEffectiveAppliedDiscountId({
  storedAppliedId,
  assignedGlobalId,
  propertyId,
  groupIndex,
}) {
  const fromGroup = resolveGroupPropertyDiscountId(assignedGlobalId, propertyId, groupIndex);
  if (fromGroup) return fromGroup;

  if (!propertyId || !groupIndex) {
    return storedAppliedId ? String(storedAppliedId) : null;
  }

  if (storedAppliedId) {
    const resolved = groupIndex.resolveForProperty(storedAppliedId, propertyId);
    if (resolved) return resolved;
  }

  return storedAppliedId ? String(storedAppliedId) : null;
}

export async function fetchSimpraCheck(checkId, propertyId) {
  const id = String(checkId || '').trim();
  if (!id) throw new Error('Check ID yok');

  const pid = String(propertyId || '').trim();
  const res = await callEdgeFunction('simpra-proxy', {
    method: 'POST',
    body: {
      method: 'GET',
      path: `/checks/${id}`,
      ...(pid ? { propertyId: pid } : {}),
    },
  });

  const check = parseSimpraCheckResponse(res);
  if (!check) {
    const msg = typeof res?.error === 'string' ? res.error : 'Simpra check yanıtı okunamadı';
    throw new Error(msg);
  }
  return check;
}

export function formatPerson(person) {
  if (!person) return '—';
  const name = [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
  return name || person.name || '—';
}

export function formatSimpraMoney(val) {
  if (val === null || val === undefined || val === '') return '—';
  const n = Number(val);
  if (!Number.isFinite(n)) return String(val);
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function unixToLocale(unix) {
  if (unix == null || unix === '') return '—';
  const n = Number(unix);
  if (!Number.isFinite(n) || n <= 0) return '—';
  try {
    return new Date(n * 1000).toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}
